import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import ExcelUploadModal from '../ExcelUploadModal';

// Funkce pro formátování čísel s mezerami jako oddělovači tisíců
const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(num);
};

export default function CashbackStrategicTab({ userRole, users, currentUserId, isDarkMode }) {
  const [cashbackStrategicCommissions, setCashbackStrategicCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(userRole === 'admin' ? '' : currentUserId);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualCommission, setManualCommission] = useState({
    companyName: '',
    ico: '',
    productType: '',
    gp: '',
    userId: '',
    month: getCurrentMonth()
  });

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  const fetchCashbackStrategicCommissions = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/cashback-strategic-commissions?month=${selectedMonth}&userId=${currentUserId}`;
      if (selectedUser) {
        url += `&filterUserId=${selectedUser}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCashbackStrategicCommissions(data.commissions || []);
      }
    } catch (error) {
      console.error('Chyba při načítání cashback strategických provizí:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${API_URL}/cashback-strategic-commissions/months?userId=${currentUserId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMonths(data.months || []);
      }
    } catch (error) {
      console.error('Chyba při načítání měsíců:', error);
    }
  };

  const deleteCommission = async (commissionId) => {
    try {
      const response = await fetch(`${API_URL}/cashback-strategic-commissions/${commissionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Cashback strategická provize byla úspěšně smazána');
        await fetchCashbackStrategicCommissions();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání provize: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při mazání provize:', error);
      alert('Chyba při mazání provize');
    }
  };

  const assignUserToCommission = async (commissionId, userId) => {
    try {
      const response = await fetch(`${API_URL}/cashback-strategic-commissions/${commissionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        await fetchCashbackStrategicCommissions();
      } else {
        alert('Chyba při přiřazování uživatele');
      }
    } catch (error) {
      console.error('Chyba při přiřazování uživatele:', error);
      alert('Chyba při přiřazování uživatele');
    }
  };

  const deleteMonth = async () => {
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    const confirmMessage = `Opravdu chcete smazat všechny cashback strategické provize pro měsíc ${monthName}? Tato akce je nevratná!\n\nPočet provizí ke smazání: ${cashbackStrategicCommissions.length}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cashback-strategic-commissions/month/${selectedMonth}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Úspěšně smazáno ${data.deletedCount} cashback strategických provizí pro měsíc ${monthName}`);
        await fetchCashbackStrategicCommissions();
        await fetchAvailableMonths();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při mazání měsíce');
      console.error('Chyba při mazání měsíce:', error);
    } finally {
      setLoading(false);
    }
  };

  const addManualCashbackStrategicCommission = async () => {
    if (!manualCommission.companyName || !manualCommission.productType || !manualCommission.gp || !manualCommission.userId) {
      alert('Vyplňte prosím název firmy, typ produktu, GP a vyberte uživatele');
      return;
    }

    try {
      // Získej strategický koeficient uživatele
      const coeffResponse = await fetch(`${API_URL}/user/coefficients?userId=${manualCommission.userId}`);
      let strategicCoefficient = 1.0;
      if (coeffResponse.ok) {
        const coeffData = await coeffResponse.json();
        strategicCoefficient = coeffData.strategicCoefficient || 1.0;
      }

      const gp = parseFloat(manualCommission.gp);
      const commission = gp * strategicCoefficient;

      const response = await fetch(`${API_URL}/cashback-strategic-commissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: manualCommission.companyName,
          ico: manualCommission.ico || '',
          productType: manualCommission.productType,
          gp: gp,
          commission: commission,
          coefficient: strategicCoefficient,
          userId: manualCommission.userId,
          month: manualCommission.month
        })
      });

      if (response.ok) {
        alert('Cashback strategická provize byla úspěšně přidána');
        setShowManualAddModal(false);
        setManualCommission({
          companyName: '',
          ico: '',
          productType: '',
          gp: '',
          userId: '',
          month: getCurrentMonth()
        });
        fetchCashbackStrategicCommissions();
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání provize: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při přidávání provize:', error);
      alert('Chyba při přidávání provize');
    }
  };

  useEffect(() => {
    fetchCashbackStrategicCommissions();
    fetchAvailableMonths();
  }, [selectedMonth, selectedUser]);

  // Výpočet souhrnů
  const totalGP = cashbackStrategicCommissions.reduce((sum, c) => sum + (c.gp || 0), 0);
  const totalCommission = cashbackStrategicCommissions.reduce((sum, c) => sum + (c.commission || 0), 0);
  
  // Najdi koeficient z první provize s nenulowym GP
  const displayedCoefficient = cashbackStrategicCommissions.find(c => c.gp && c.gp > 0 && c.coefficient)?.coefficient || null;

  return (
    <div>
      <div className={`mb-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow-sm border`}>
        <div className="flex items-center gap-4">
          <div>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mr-2`}>Měsíc:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`border rounded px-3 py-1 text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
            >
              <option value={getCurrentMonth()}>Aktuální měsíc</option>
              {[...availableMonths].reverse().map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          {userRole === 'admin' && (
            <>
              <div>
                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mr-2`}>Uživatel:</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className={`border rounded px-3 py-1 text-sm min-w-[150px] ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Všichni uživatelé</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              {userRole === 'admin' && (
                <>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium"
                  >
                    📤 Nahrát Excel
                  </button>
                  <button
                    onClick={() => setShowManualAddModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
                  >
                    ➕ Přidat provizi ručně
                  </button>
                </>
              )}
              <button
                onClick={fetchCashbackStrategicCommissions}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-50 mr-2"
              >
                {loading ? 'Načítání...' : 'Obnovit'}
              </button>
              {userRole === 'admin' && cashbackStrategicCommissions.length > 0 && (
                <button
                  onClick={deleteMonth}
                  disabled={loading}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  🗑️ Smazat celý měsíc
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Souhrny cashback strategických provizí */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-green-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Celkové GP</h3>
          <p className="text-2xl font-bold text-green-600">{formatNumber(totalGP)} Kč</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-purple-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Cashback strategické provize</h3>
          <p className="text-2xl font-bold text-purple-600">{formatNumber(totalCommission)} Kč</p>
        </div>
        {selectedUser && (
          <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-orange-500`}>
            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Koeficient</h3>
            <p className="text-2xl font-bold text-orange-600">
              {displayedCoefficient ? `${displayedCoefficient}×` : '—'}
            </p>
          </div>
        )}
        <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-blue-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Koeficient × počet záznamů</h3>
          <p className="text-2xl font-bold text-blue-600">
            {displayedCoefficient ? `${displayedCoefficient}× × ${formatNumber(cashbackStrategicCommissions.length)}` : formatNumber(cashbackStrategicCommissions.length)}
          </p>
        </div>
      </div>

      {/* Tabulka */}
      {loading ? (
        <div className="text-center py-8">Načítání cashback strategických provizí...</div>
      ) : (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          {cashbackStrategicCommissions.length === 0 ? (
            userRole === 'admin' ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádné cashback strategické provize pro vybraný měsíc.
                <br />
                Použijte tlačítko "📤 Nahrát Excel" pro import dat.
              </div>
            ) : null
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Název firmy</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>IČO</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Typ produktu</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>GP</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Provize</th>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    {userRole === 'admin' && (
                      <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Akce</th>
                    )}
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {cashbackStrategicCommissions.map((commission) => (
                    <tr key={commission._id} className={`${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-50'}`}>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-white' : ''}`}>{commission.companyName}</td>
                      <td className={`px-3 py-2 text-sm text-center font-mono ${isDarkMode ? 'text-white' : ''}`}>{commission.ico || '-'}</td>
                      <td className="px-3 py-2 text-sm text-center">
                        <span className={`${isDarkMode ? 'bg-purple-700 text-purple-200' : 'bg-purple-100 text-purple-800'} px-2 py-1 rounded text-xs`}>
                          {commission.productType}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-sm text-center font-medium ${isDarkMode ? 'text-white' : ''}`}>{commission.gp ? formatNumber(commission.gp) : '0'} Kč</td>
                      <td className="px-3 py-2 text-sm text-center font-bold text-green-600">{commission.commission ? formatNumber(commission.commission) : '0'} Kč</td>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-white' : ''}`}>
                        {userRole === 'admin' ? (
                          editingUserId === commission._id ? (
                            <select 
                              className={`text-sm border rounded px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white border-gray-300'}`}
                              onChange={(e) => {
                                if (e.target.value) {
                                  assignUserToCommission(commission._id, e.target.value);
                                  setEditingUserId(null);
                                }
                              }}
                              onBlur={() => setEditingUserId(null)}
                              value={commission.userId || ""}
                              autoFocus
                            >
                              <option value="" disabled>Vyberte uživatele...</option>
                              {users.map(user => (
                                <option key={user._id} value={user._id}>
                                  {user.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span 
                              onClick={() => setEditingUserId(commission._id)}
                              className="text-blue-600 cursor-pointer hover:underline"
                            >
                              {commission.userName || 'Vyberte uživatele...'}
                            </span>
                          )
                        ) : (
                          <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                            {commission.userName || '-'}
                          </span>
                        )}
                      </td>
                      {userRole === 'admin' && (
                        <td className="px-3 py-2 text-sm text-center">
                          <button
                            onClick={() => {
                              if (window.confirm(`Opravdu chcete smazat cashback strategickou provizi pro firmu "${commission.companyName}"?`)) {
                                deleteCommission(commission._id);
                              }
                            }}
                            className="text-red-600 hover:text-red-800 font-medium"
                            title="Smazat provizi"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Excel Upload Modal */}
      {showUploadModal && (
        <ExcelUploadModal
          selectedMonth={selectedMonth}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchCashbackStrategicCommissions();
            fetchAvailableMonths();
          }}
          type="cashback-strategic"
        />
      )}

      {/* Manual Add Commission Modal */}
      {showManualAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
            <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Přidat cashback strategickou provizi ručně
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Název firmy *
                </label>
                <input
                  type="text"
                  value={manualCommission.companyName}
                  onChange={(e) => setManualCommission({...manualCommission, companyName: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. ABC s.r.o."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  IČO
                </label>
                <input
                  type="text"
                  value={manualCommission.ico}
                  onChange={(e) => setManualCommission({...manualCommission, ico: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="12345678"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Typ produktu *
                </label>
                <input
                  type="text"
                  value={manualCommission.productType}
                  onChange={(e) => setManualCommission({...manualCommission, productType: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. Mobilní tarif"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  GP (Kč) *
                </label>
                <input
                  type="number"
                  value={manualCommission.gp}
                  onChange={(e) => setManualCommission({...manualCommission, gp: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. 5000"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Uživatel *
                </label>
                <select
                  value={manualCommission.userId}
                  onChange={(e) => setManualCommission({...manualCommission, userId: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Vyberte uživatele</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Měsíc *
                </label>
                <input
                  type="month"
                  value={manualCommission.month}
                  onChange={(e) => setManualCommission({...manualCommission, month: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowManualAddModal(false);
                  setManualCommission({
                    companyName: '',
                    ico: '',
                    productType: '',
                    gp: '',
                    userId: '',
                    month: getCurrentMonth()
                  });
                }}
                className={`px-4 py-2 rounded text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Zrušit
              </button>
              <button
                onClick={addManualCashbackStrategicCommission}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
              >
                Přidat provizi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
